// ./src/Pages/Policies/Versions/Form/Components/CreativeFileUpload.jsx
import { useState, useEffect, useRef } from 'react';
import { Box, Typography, LinearProgress, FormHelperText } from '@mui/material';
import { ButtonForm } from 'Components/Form/styles';

const formatBytes = bytes => {
  if (!bytes) return '0 B';
  const k = 1024, units = ['B','KB','MB','GB','TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
};

const isAccepted = (file, accept) => {
  if (!accept) return true;
  const rules = accept.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
  const mime = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  return rules.some(rule => rule.startsWith('.') ? name.endsWith(rule) : mime === rule);
};

function CreativeFileUpload({
  id = 'attachment-upload',
  value,
  onChange,
  accept = '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  maxSizeMB = 25,
  helperText = '',
  error = false,
  disabled = false,
}) {
  const [dragOver, setDragOver] = useState(false);
  const [localError, setLocalError] = useState('');
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const handlePick = () => inputRef.current?.click();

  const validateAndSet = file => {
    setLocalError('');
    if (!file) return;
    if (!isAccepted(file, accept)) return setLocalError('Tipo de arquivo não suportado.');
    if (file.size > maxSizeMB * 1024 * 1024) return setLocalError(`Arquivo maior que ${maxSizeMB}MB.`);
    setProgress(10);
    const t = setInterval(() => {
      setProgress(p => {
        if (p >= 100) { clearInterval(t); return 100; }
        return p + 15;
      });
    }, 60);
    onChange(file);
  };

  const onInputChange = e => {
    const file = e.target.files?.[0];
    if (file) validateAndSet(file);
    e.target.value = null;
  };

  const onDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) validateAndSet(file);
  };

  const onPaste = e => {
    const item = [...e.clipboardData.items].find(i => i.kind === 'file');
    const file = item?.getAsFile?.();
    if (file) validateAndSet(file);
  };

  useEffect(() => {
    if (!value) setProgress(0);
    else if (progress < 100) setProgress(100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const removeFile = () => { onChange(null); setProgress(0); setLocalError(''); };
  const openPreview = () => {
    if (!value) return;
    const url = URL.createObjectURL(value);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const fileEmoji = !value ? '📄' : (value.type === 'application/pdf' ? '📄' : '📝');

  return (
    <Box sx={{ width: '100%' }}>
      <input
        id={id}
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={onInputChange}
        disabled={disabled}
      />

      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={e => !disabled && (e.key === 'Enter' || e.key === ' ') && handlePick()}
        onClick={() => !disabled && handlePick()}
        onDrop={e => !disabled && onDrop(e)}
        onDragOver={e => { if (!disabled) { e.preventDefault(); setDragOver(true); } }}
        onDragLeave={() => !disabled && setDragOver(false)}
        onPaste={e => !disabled && onPaste(e)}
        aria-label="Enviar arquivo. Clique, solte um arquivo aqui, ou cole do clipboard."
        aria-disabled={disabled}
        sx={{
          position: 'relative',
          borderRadius: 2,
          p: 3,
          cursor: disabled ? 'not-allowed' : 'pointer',
          userSelect: 'none',
          opacity: disabled ? .6 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          background: dragOver ? 'rgba(25,118,210,0.06)' : '#fafafa',
          boxShadow: dragOver ? 'inset 0 0 0 2px rgba(25,118,210,.35)' : 'none',
          transition: 'all .25s ease',
          backgroundImage: dragOver
            ? 'linear-gradient(90deg, rgba(25,118,210,.12), rgba(156,39,176,.12), rgba(0,150,136,.12))'
            : 'none',
          '@keyframes dash': {
            '0%': { backgroundPosition: '0% 50%' },
            '100%': { backgroundPosition: '200% 50%' },
          },
          backgroundSize: dragOver ? '200% 100%' : 'auto',
          animation: dragOver ? 'dash 1.2s linear infinite' : 'none',
        }}
        >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: value ? 1 : 0 }}>
          <Box sx={{ fontSize: 36, lineHeight: 1 }}>{fileEmoji}</Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {value ? 'Arquivo selecionado' : 'Arraste e solte, clique para escolher, ou cole (Ctrl/Cmd+V)'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {value ? `${value.name} · ${formatBytes(value.size)}` : `PDF, DOC ou DOCX · até ${maxSizeMB}MB`}
            </Typography>
          </Box>

            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            {!value && ButtonForm && (
                <ButtonForm
                as="span"
                variant="button"
                style={{ minWidth: 160 }}
                title="Escolher arquivo"
                label="Selecionar arquivo"
                />
            )}

            {value && ButtonForm && (
                <>
                <ButtonForm
                    type="button"
                    variant="button"
                    onClick={openPreview}
                    style={{ minWidth: 120 }}
                    title="Abrir pré-visualização"
                    label="Visualizar"
                />
                <ButtonForm
                    type="button"
                    variant="button"
                    onClick={handlePick}
                    style={{ minWidth: 100 }}
                    title="Escolher outro arquivo"
                    label="Trocar"
                />
                <ButtonForm
                    type="button"
                    secondary
                    onClick={removeFile}
                    style={{ minWidth: 100 }}
                    title="Remover arquivo"
                    label="Remover"
                />
                </>
            )}
            </Box>
        </Box>

        {value && (
          <Box sx={{ mt: 1 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {progress < 100 ? 'Validando…' : 'Pronto para enviar'}
            </Typography>
          </Box>
        )}
      </Box>

      <div aria-live="polite">
        {(error || localError) ? (
          <FormHelperText error sx={{ mt: 1 }}>
            {localError || helperText}
          </FormHelperText>
        ) : helperText ? (
          <FormHelperText sx={{ mt: 1 }}>{helperText}</FormHelperText>
        ) : null}
      </div>
    </Box>
  );
}

export default CreativeFileUpload;
